import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BellIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  EyeIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { useWebSocketContext } from '@/components/providers/WebSocketProvider';
import { notificationsService } from '@/services/notifications.service';
import type { Notification, NotificationType } from '@/types/notifications';
import toast from 'react-hot-toast';

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'incident':
      return ExclamationTriangleIcon;
    case 'threat':
      return EyeIcon;
    case 'executive_protection':
      return ShieldCheckIcon;
    case 'investigation':
      return MagnifyingGlassIcon;
    default:
      return BellIcon;
  }
};

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'critical':
      return 'text-danger-600 dark:text-danger-400';
    case 'high':
      return 'text-orange-600 dark:text-orange-400';
    case 'medium':
      return 'text-warning-600 dark:text-warning-400';
    case 'low':
      return 'text-success-600 dark:text-success-400';
    default:
      return 'text-secondary-600 dark:text-secondary-400';
  }
};

const getSeverityBg = (severity: string, isRead: boolean) => {
  const baseClass = isRead ? 'opacity-60' : '';

  switch (severity) {
    case 'critical':
      return `bg-danger-50 border-l-danger-500 dark:bg-danger-900/20 dark:border-l-danger-400 ${baseClass}`;
    case 'high':
      return `bg-orange-50 border-l-orange-500 dark:bg-orange-900/20 dark:border-l-orange-400 ${baseClass}`;
    case 'medium':
      return `bg-warning-50 border-l-warning-500 dark:bg-warning-900/20 dark:border-l-warning-400 ${baseClass}`;
    case 'low':
      return `bg-success-50 border-l-success-500 dark:bg-success-900/20 dark:border-l-success-400 ${baseClass}`;
    default:
      return `bg-secondary-50 border-l-secondary-500 dark:bg-secondary-800/50 dark:border-l-secondary-400 ${baseClass}`;
  }
};

export default function NotificationCenter({ isOpen, onClose }: NotificationCenterProps) {
  const { connectionState, messages } = useWebSocketContext();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const queryClient = useQueryClient();

  // Fetch notifications
  const { data: notificationsData, isLoading, isError, error } = useQuery({
    queryKey: ['notifications', filter],
    queryFn: () => notificationsService.getNotifications({
      read: filter === 'unread' ? false : undefined,
      limit: 50,
    }),
    enabled: isOpen,
    refetchInterval: 10000, // Refetch every 10 seconds
    retry: 2, // Retry failed requests twice
  });

  const notifications = notificationsData?.data ?? [];
  const unreadCount = notificationsData?.meta?.unread ?? 0;

  // Listen for WebSocket notification events and invalidate queries
  useEffect(() => {
    if (messages.length > 0) {
      const latestMessage = messages[0];
      // Check for any notification type (incident, threat, alert, notification, etc.)
      if (['incident', 'threat', 'alert', 'notification', 'executive_protection', 'investigation'].includes(latestMessage.type)) {
        // Invalidate both queries to refresh the notification list and count
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      }
    }
  }, [messages, queryClient]);

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => notificationsService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationsService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      toast.success('All notifications marked as read');
    },
  });

  // Clear all mutation
  const clearAllMutation = useMutation({
    mutationFn: () => notificationsService.clearAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      toast.success('All notifications cleared');
    },
  });

  const handleMarkAsRead = (notification: Notification) => {
    if (!notification.read) {
      markAsReadMutation.mutate(notification.id);
    }
  };

  const handleMarkAllAsRead = () => {
    if (unreadCount > 0) {
      markAllAsReadMutation.mutate();
    }
  };

  const handleClearAll = () => {
    if (notifications.length > 0) {
      clearAllMutation.mutate();
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) { return 'Just now'; }
    if (diffInMinutes < 60) { return `${diffInMinutes}m ago`; }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) { return `${diffInHours}h ago`; }

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const containerVariants = {
    hidden: {
      opacity: 0,
      x: '100%',
      transition: {
        duration: 0.2,
      },
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.2 },
    },
    exit: {
      opacity: 0,
      x: 100,
      transition: { duration: 0.1 },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          />

          {/* Notification Panel */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white dark:bg-secondary-900 border-l border-secondary-200 dark:border-secondary-700 shadow-xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="p-4 border-b border-secondary-200 dark:border-secondary-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <BellIcon className="w-6 h-6 text-secondary-600 dark:text-secondary-400" />
                  <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
                    Notifications
                  </h2>
                  {unreadCount > 0 && (
                    <span className="px-2 py-1 text-xs bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300 rounded-full">
                      {unreadCount}
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={onClose}
                  className="text-secondary-500 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-200"
                >
                  <XMarkIcon className="w-5 h-5" />
                </Button>
              </div>

              {/* Connection Status */}
              <div className="flex items-center space-x-2 mt-3">
                <div
                  className={cn(
                    'w-2 h-2 rounded-full',
                    connectionState === 'connected' && 'bg-success-500',
                    connectionState === 'connecting' && 'bg-warning-500',
                    connectionState === 'disconnected' && 'bg-secondary-400',
                    connectionState === 'error' && 'bg-danger-500'
                  )}
                />
                <span className="text-xs text-secondary-600 dark:text-secondary-400">
                  Real-time updates {connectionState}
                </span>
              </div>
            </div>

            {/* Filters */}
            <div className="p-4 border-b border-secondary-200 dark:border-secondary-700">
              <div className="flex space-x-1">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'unread', label: 'Unread' },
                ].map((filterOption) => (
                  <button
                    key={filterOption.key}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onClick={() => setFilter(filterOption.key as any)}
                    className={cn(
                      'px-3 py-1 text-xs font-medium rounded-full transition-colors',
                      filter === filterOption.key
                        ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/20 dark:text-primary-100'
                        : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:text-secondary-100 dark:hover:bg-secondary-800'
                    )}
                  >
                    {filterOption.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            {(notifications.length > 0 || unreadCount > 0) && (
              <div className="p-4 border-b border-secondary-200 dark:border-secondary-700 flex space-x-2">
                {unreadCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleMarkAllAsRead}
                    disabled={markAllAsReadMutation.isPending}
                    className="flex-1"
                  >
                    <CheckCircleIcon className="w-4 h-4 mr-2" />
                    Mark All Read
                  </Button>
                )}
                {notifications.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearAll}
                    disabled={clearAllMutation.isPending}
                    className="flex-1"
                  >
                    Clear All
                  </Button>
                )}
              </div>
            )}

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-secondary-500 dark:text-secondary-400">Loading...</div>
                </div>
              ) : isError ? (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <ExclamationTriangleIcon className="w-16 h-16 text-danger-300 dark:text-danger-600 mb-4" />
                  <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                    Failed to load notifications
                  </h3>
                  <p className="text-secondary-600 dark:text-secondary-400 text-sm mb-4">
                    {error instanceof Error ? error.message : 'An unexpected error occurred'}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => queryClient.invalidateQueries({ queryKey: ['notifications'] })}
                  >
                    Try Again
                  </Button>
                </div>
              ) : notifications.length > 0 ? (
                <div className="space-y-2 p-4">
                  <AnimatePresence>
                    {notifications.map((notification) => {
                      const Icon = getNotificationIcon(notification.type);
                      return (
                        <motion.div
                          key={notification.id}
                          variants={itemVariants}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          layout
                          onClick={() => handleMarkAsRead(notification)}
                          className={cn(
                            'p-4 border-l-4 rounded-lg cursor-pointer transition-all hover:shadow-md',
                            getSeverityBg(notification.severity, notification.read),
                            notification.read && 'opacity-60'
                          )}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={cn('mt-0.5', getSeverityColor(notification.severity))}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className={cn(
                                  'font-medium text-secondary-900 dark:text-white text-sm',
                                  notification.read && 'font-normal'
                                )}>
                                  {notification.title}
                                  {!notification.read && (
                                    <span className="ml-2 inline-block w-2 h-2 bg-primary-600 rounded-full"></span>
                                  )}
                                </h4>
                                <span className="text-xs text-secondary-500 dark:text-secondary-400 flex-shrink-0 ml-2">
                                  {formatTimestamp(notification.createdAt)}
                                </span>
                              </div>
                              <p className="text-sm text-secondary-700 dark:text-secondary-300">
                                {notification.message}
                              </p>
                              <div className="flex items-center justify-between mt-2">
                                <span className={cn('text-xs font-medium', getSeverityColor(notification.severity))}>
                                  {notification.severity.toUpperCase()}
                                </span>
                                <span className="text-xs text-secondary-500 dark:text-secondary-400">
                                  {notification.type.replace('_', ' ').toUpperCase()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <BellIcon className="w-16 h-16 text-secondary-300 dark:text-secondary-600 mb-4" />
                  <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                    No notifications
                  </h3>
                  <p className="text-secondary-600 dark:text-secondary-400 text-sm">
                    {filter === 'all'
                      ? "You're all caught up! New notifications will appear here."
                      : `No ${filter} notifications at this time.`
                    }
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
